export const API_ERROR_CODES = [
  'not_found',
  'unauthorized',
  'validation',
  'forbidden',
  'conflict',
  'offline',
  'timeout',
  'server',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

const STATUS: Record<ApiErrorCode, number> = {
  not_found: 404,
  unauthorized: 401,
  validation: 422,
  forbidden: 403,
  conflict: 409,
  offline: 0,
  timeout: 504,
  server: 500,
};

/** Shown verbatim in error states, so it is written for a person, in Spanish. */
const MESSAGES: Record<ApiErrorCode, string> = {
  not_found: 'No encontramos ese registro.',
  unauthorized: 'Tu sesión expiró. Vuelve a entrar.',
  validation: 'Revisa los datos: hay campos con problemas.',
  forbidden: 'Tu plan o tu rol no permite esta acción.',
  conflict: 'Alguien más modificó este registro. Vuelve a cargarlo.',
  offline: 'Sin conexión con el servidor.',
  timeout: 'El servidor demoró demasiado en responder.',
  server: 'Algo falló de nuestro lado.',
};

export class ApiError extends Error {
  readonly status: number;

  constructor(
    readonly code: ApiErrorCode,
    message?: string,
    /** Field path → messages, ready to hand to react-hook-form. */
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message ?? MESSAGES[code]);
    this.name = 'ApiError';
    this.status = STATUS[code];
  }

  /** Retrying a validation or permission failure only repeats the failure. */
  get retryable(): boolean {
    return this.code === 'offline' || this.code === 'timeout' || this.code === 'server';
  }

  static notFound(entity: string, id: string): ApiError {
    return new ApiError('not_found', `No existe ${entity} con id ${id}.`);
  }

  static validation(fieldErrors: Record<string, string[]>): ApiError {
    return new ApiError('validation', MESSAGES.validation, fieldErrors);
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;
